import type { RagTelemetryOverview } from '@callhub/shared';

export type RagTelemetryAlertSeverity = 'watch' | 'critical';

export type RagTelemetryAlert = {
  id: 'sample_size' | 'high_rejection' | 'low_acceptance' | 'proposal_backlog';
  severity: RagTelemetryAlertSeverity;
  title: string;
  detail: string;
  currentValue: string;
  thresholdGuidance: string;
  runbook: string;
  runbookHref: '#rag-telemetry-playbook';
  runbookLabel: 'Open RAG runbook';
};

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

export function buildRagTelemetryAlerts(telemetry: RagTelemetryOverview): RagTelemetryAlert[] {
  const alerts: RagTelemetryAlert[] = [];
  const feedback = telemetry.feedback;
  const totalSuggestionStates =
    telemetry.suggestionStatus.proposed +
    telemetry.suggestionStatus.applied +
    telemetry.suggestionStatus.accepted +
    telemetry.suggestionStatus.rejected;
  const proposedShare =
    totalSuggestionStates > 0 ? telemetry.suggestionStatus.proposed / totalSuggestionStates : 0;

  if (feedback.total < 8) {
    alerts.push({
      id: 'sample_size',
      severity: 'watch',
      title: 'Low feedback volume',
      detail:
        'Current acceptance/rejection rates are based on a small sample and may be noisy this window.',
      currentValue: `${telemetry.feedback.total} feedback votes captured in the selected window.`,
      thresholdGuidance:
        'Tune RAG thresholds only after at least 8 votes or a longer lookback window confirms the same trend.',
      runbook: 'Increase sampling window or gather at least 8 operator votes before tuning models.',
      runbookHref: '#rag-telemetry-playbook',
      runbookLabel: 'Open RAG runbook',
    });
  }

  if (feedback.total >= 8 && feedback.rejectionRate >= 0.55) {
    alerts.push({
      id: 'high_rejection',
      severity: 'critical',
      title: 'High rejection rate',
      detail: 'Operators are rejecting most suggestions for this lookback period.',
      currentValue: `${formatPercent(feedback.rejectionRate)} rejection across ${feedback.total} feedback votes.`,
      thresholdGuidance:
        'Critical at >=55% rejection with at least 8 votes. Raise Min confidence or lower Top-K before widening context.',
      runbook:
        'Review prompt template and retrieval thresholds immediately, then validate top rejected conversations.',
      runbookHref: '#rag-telemetry-playbook',
      runbookLabel: 'Open RAG runbook',
    });
  } else if (feedback.total >= 8 && feedback.acceptanceRate < 0.4) {
    alerts.push({
      id: 'low_acceptance',
      severity: 'watch',
      title: 'Low acceptance trend',
      detail: 'Suggestion usefulness appears below target for the selected lookback period.',
      currentValue: `${formatPercent(feedback.acceptanceRate)} acceptance across ${feedback.total} feedback votes.`,
      thresholdGuidance:
        'Watch below 40% acceptance with at least 8 votes. Tighten Min confidence and Max suggestions before expanding retrieval breadth.',
      runbook:
        'Inspect rejected samples, tighten retrieval context, and retune confidence/min-context settings.',
      runbookHref: '#rag-telemetry-playbook',
      runbookLabel: 'Open RAG runbook',
    });
  }

  if (telemetry.suggestionStatus.proposed >= 8 && proposedShare >= 0.55) {
    alerts.push({
      id: 'proposal_backlog',
      severity: 'watch',
      title: 'Proposal backlog risk',
      detail: 'A high share of suggestions remain in proposed state without operator resolution.',
      currentValue: `${telemetry.suggestionStatus.proposed} proposed suggestions remain unresolved (${formatPercent(proposedShare)} of visible states).`,
      thresholdGuidance:
        'Watch when at least 8 suggestions remain proposed and unresolved share reaches 55%. Fix operator workflow before loosening retrieval thresholds.',
      runbook:
        'Audit dialer workflows to ensure accept/reject actions are being captured consistently.',
      runbookHref: '#rag-telemetry-playbook',
      runbookLabel: 'Open RAG runbook',
    });
  }

  return alerts;
}

export function resolveRagTelemetryHealth(alerts: RagTelemetryAlert[]): {
  severity: 'healthy' | 'watch' | 'critical';
  label: 'Healthy' | 'Watch' | 'Critical';
} {
  if (alerts.some((alert) => alert.severity === 'critical')) {
    return { severity: 'critical', label: 'Critical' };
  }

  if (alerts.length > 0) {
    return { severity: 'watch', label: 'Watch' };
  }

  return { severity: 'healthy', label: 'Healthy' };
}
